import { useState, useCallback } from 'react';
import { StoreProductsService } from '../services/store-products';

// Dados iniciais do produto
const INITIAL_PRODUCT = {
  name: '',
  currency: 'BRL',
  amount: '',
  unique_name: '',
  installments_max: '0',
  installments_tax: [],
  type: 'NotConsumable',
};

/**
 * Hook personalizado para gerenciar formulário de produtos
 * @param {string} connectorUid - UID do connector
 * @param {Object} initialData - Dados iniciais (para edição)
 * @returns {Object} Estado e funções para gerenciar o formulário
 */
export const useProductForm = (connectorUid, initialData = null) => {
  const [product, setProduct] = useState(initialData || INITIAL_PRODUCT);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});

  // Campos obrigatórios
  const requiredFields = ['name', 'currency', 'amount', 'unique_name'];

  // Validar formulário
  const validateForm = useCallback(() => {
    const newErrors = {};

    // Validar campos obrigatórios
    requiredFields.forEach(field => {
      if (!product[field]?.toString().trim()) {
        newErrors[field] = 'Este campo é obrigatório';
      }
    });

    // Validar valor
    if (product.amount && (isNaN(parseFloat(product.amount)) || parseFloat(product.amount) <= 0)) {
      newErrors.amount = 'Valor deve ser um número maior que zero';
    }

    // Validar nome único (formato básico)
    if (product.unique_name && !/^[a-zA-Z0-9_-]+$/.test(product.unique_name)) {
      newErrors.unique_name = 'Use apenas letras, números, hífens e underscores';
    }

    // Validar parcelas
    const maxInstallments = parseInt(product.installments_max);
    if (maxInstallments > 12) {
      newErrors.installments_max = 'Máximo de 12 parcelas';
    }

    // Validar juros das parcelas
    if (maxInstallments > 0 && product.installments_tax) {
      product.installments_tax.forEach((tax, index) => {
        if (tax && (isNaN(parseFloat(tax)) || parseFloat(tax) < 0)) {
          newErrors[`installment_${index}`] = 'Juros deve ser um número positivo';
        }
      });
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [product, requiredFields]);

  // Verificar se o formulário é válido
  const isFormValid = useCallback(() => {
    return requiredFields.every(field => Boolean(product[field]?.toString().trim())) &&
           !isNaN(parseFloat(product.amount)) &&
           parseFloat(product.amount) > 0;
  }, [product, requiredFields]);

  // Atualizar campo do produto
  const updateField = useCallback((field, value) => {
    if (field === 'installments_max') {
      const maxInstallments = parseInt(value) || 0;
      const currentInstallments = product.installments_tax || [];
      
      setProduct(prev => ({
        ...prev,
        installments_max: value,
        installments_tax: currentInstallments.slice(0, maxInstallments),
      }));
      return;
    }

    setProduct(prev => ({
      ...prev,
      [field]: value
    }));

    // Limpar erro do campo quando o usuário digitar
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: null
      }));
    }
  }, [product.installments_tax, errors]);

  // Atualizar juros de parcela específica
  const updateInstallment = useCallback((index, value) => {
    const newInstallmentTax = [...(product.installments_tax || [])];
    newInstallmentTax[index] = value;

    setProduct(prev => ({
      ...prev,
      installments_tax: newInstallmentTax
    }));

    // Limpar erro da parcela
    const errorKey = `installment_${index}`;
    if (errors[errorKey]) {
      setErrors(prev => ({
        ...prev,
        [errorKey]: null
      }));
    }
  }, [product.installments_tax, errors]);

  // Salvar produto (criar ou atualizar)
  const saveProduct = useCallback(async (isEdit = false, productUid = null) => {
    if (!validateForm()) {
      return { success: false, message: 'Corrija os erros no formulário' };
    }

    setSaving(true);

    try {
      // Preparar dados do produto
      const productData = {
        ...product,
        amount: parseFloat(product.amount) || 0,
        installments_max: parseInt(product.installments_max) || 0,
        installments_tax: (product.installments_tax || []).map(tax => parseFloat(tax) || 0),
      };

      let result;
      if (isEdit && productUid) {
        result = await StoreProductsService.updateProduct(productUid, productData);
      } else {
        result = await StoreProductsService.createProduct(connectorUid, productData);
      }

      return { 
        success: true, 
        data: result, 
        message: isEdit ? 'Produto atualizado com sucesso!' : 'Produto criado com sucesso!' 
      };

    } catch (error) {
      console.error('Erro ao salvar produto:', error);
      return { 
        success: false, 
        message: error.message || 'Erro ao salvar produto' 
      };
    } finally {
      setSaving(false);
    }
  }, [product, connectorUid, validateForm]);

  // Carregar produto para edição
  const loadProduct = useCallback(async (productUid) => {
    setLoading(true);
    
    try {
      const result = await StoreProductsService.getProductByUid(productUid);
      if (result.success) {
        setProduct(result.data);
        return { success: true };
      } else {
        return { success: false, message: result.message };
      }
    } catch (error) {
      console.error('Erro ao carregar produto:', error);
      return { success: false, message: 'Erro ao carregar produto' };
    } finally {
      setLoading(false);
    }
  }, []);

  // Resetar formulário
  const resetForm = useCallback(() => {
    setProduct(INITIAL_PRODUCT);
    setErrors({});
  }, []);

  // Gerar nome único baseado no nome
  const generateUniqueName = useCallback(() => {
    if (product.name) {
      const uniqueName = product.name
        .toLowerCase()
        .replace(/[^a-z0-9\s-]/g, '') // Remove caracteres especiais
        .replace(/\s+/g, '-') // Substitui espaços por hífens
        .replace(/-+/g, '-') // Remove hífens duplicados
        .replace(/^-|-$/g, ''); // Remove hífens do início e fim
      
      updateField('unique_name', uniqueName);
    }
  }, [product.name, updateField]);

  return {
    // Estado
    product,
    loading,
    saving,
    errors,
    
    // Validação
    isFormValid: isFormValid(),
    validateForm,
    
    // Ações
    updateField,
    updateInstallment,
    saveProduct,
    loadProduct,
    resetForm,
    generateUniqueName,
    
    // Utilitários
    requiredFields,
    countInstallments: parseInt(product.installments_max) || 0,
  };
};
